import { FC, useCallback, useMemo, useState } from 'react';
import {
    Checkbox,
    Icon,
    Icons,
    IconButton,
    Label,
    RadioGroup,
    RadioGroupOption,
    RadioGroupOptionValue,
    Tabs,
    Toggle
} from '@silverfort/sf-components';

import EntitiesPickerTab from './EntitiesPickerTab';
import {
    RiskRestrictionTypes,
    ServiceAccountAction,
    ServiceAccountData,
    ServiceAccountEntity
} from '../../ServiceAccountsTypes';
import ProtocolPickerTab from './ProtocolPickerTab';
import RiskPickerTab from './RiskPickerTab';
import { useServiceAccountContext } from '../../contexts/ServiceAccountContext';
import { Pages, Roles } from '../../../../../context/contexts/UserContext/permissionsTypes';
import useHasPermissions from '../../../../../hooks/useHasPermissions';
import { RISK_RULES } from '@silverfort-nodelibs/common/utils/insight';
import InteractiveLoginPickerTab from './InteractiveLoginPickerTab';

type TabProps = { value: number; name: string; restrictionFieldName?: keyof ServiceAccountData };
const TABS: TabProps[] = [
    { value: 0, name: 'Sources', restrictionFieldName: 'restrict_devices' },
    { value: 1, name: 'Destinations', restrictionFieldName: 'restrict_resources' },
    { value: 2, name: 'Protocols' },
    { value: 3, name: 'Risk', restrictionFieldName: 'restrict_risk' }
];

enum ProtectionTabsOptions {
    UNRESTRICTED = 'unrestricted',
    RESTRICTED = 'restricted',
    INTERACTIVE = 'interactive'
}

const ProtectionSettingsTab: FC = () => {
    const { selectedServiceAccount, updatedServiceAccount, updateServiceAccountData } = useServiceAccountContext();

    const [selectedTabId, setSelectedTabId] = useState(TABS[0].value);
    const { onPremNHILicenseISPMEnabled, onPremNHILicenseITDREnabled } = useServiceAccountContext();
    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.EDITOR });

    const TabWithLicense = useMemo(() => {
        return onPremNHILicenseISPMEnabled || onPremNHILicenseITDREnabled
            ? TABS
            : TABS.filter((tab) => tab.value !== 3);
    }, [onPremNHILicenseISPMEnabled, onPremNHILicenseITDREnabled]);

    const disabledTabs = useMemo(() => {
        return onPremNHILicenseISPMEnabled || onPremNHILicenseITDREnabled ? [] : [3];
    }, [onPremNHILicenseISPMEnabled, onPremNHILicenseITDREnabled]);

    const selectedTab = useMemo(() => TABS.find((tab) => tab.value === selectedTabId), [selectedTabId]);

    const setUpdatedServiceAccount = useCallback(
        (
            field: keyof ServiceAccountData,
            value: string | string[] | boolean | number | ServiceAccountEntity[] | undefined
        ) => updateServiceAccountData(field, value, true),
        [updateServiceAccountData]
    );

    const setUpdatedEntities = useCallback(
        (type: 'devices' | 'resources', entities: ServiceAccountEntity[]) => {
            const existingEntitiesIdentifiers = (selectedServiceAccount?.[type] ?? []).map(
                (entity) => entity.identifier
            );
            const entitiesToUpdate = entities.filter(
                (entity) => entity.is_allowed || existingEntitiesIdentifiers.includes(entity.identifier)
            );
            updateServiceAccountData(type, entitiesToUpdate, true);
        },
        [selectedServiceAccount, updateServiceAccountData]
    );

    const updateAction = useCallback(
        (action: ServiceAccountAction) => {
            let updateActions = updatedServiceAccount?.actions || [];
            if (updateActions.includes(action)) {
                updateActions = updateActions.filter((a) => a !== action);
            } else {
                updateActions = [...updateActions, action];
            }
            setUpdatedServiceAccount('actions', updateActions);
        },
        [updatedServiceAccount?.actions, setUpdatedServiceAccount]
    );

    const entities = useMemo(() => {
        const map = new Map<string, ServiceAccountEntity>();
        if (selectedTab?.name === 'Sources') {
            [...(updatedServiceAccount?.devices || [])].reduce<Map<string, ServiceAccountEntity>>((map, device) => {
                map.set(device.identifier, device);
                return map;
            }, map);
        } else if (selectedTab?.name === 'Destinations') {
            [...(updatedServiceAccount?.resources || [])].reduce<Map<string, ServiceAccountEntity>>((map, resource) => {
                map.set(resource.identifier, resource);
                return map;
            }, map);
        }
        return Array.from(map.values());
    }, [selectedTab, updatedServiceAccount]);

    const isRestricted = useMemo(() => {
        if (!selectedTab?.restrictionFieldName) {
            return true; // always restricted until we implement the protocols allow all
        }

        return !!updatedServiceAccount?.[selectedTab.restrictionFieldName];
    }, [selectedTab, updatedServiceAccount]);

    const setRestriction = useCallback(
        (value: boolean) => {
            if (selectedTab?.restrictionFieldName) {
                setUpdatedServiceAccount(selectedTab.restrictionFieldName, value);
            }
        },
        [selectedTab, setUpdatedServiceAccount]
    );

    const setIsRestrictedByRiskLevel = useCallback(
        (value: boolean) => {
            if (value) {
                setUpdatedServiceAccount('risk_restriction_type', RiskRestrictionTypes.RISK_LEVEL);
                setUpdatedServiceAccount('protect_risk_indicators', []);
            } else {
                setUpdatedServiceAccount('risk_restriction_type', RiskRestrictionTypes.RISK_INDICATORS);
                setUpdatedServiceAccount('protect_risk_indicators', [RISK_RULES.SERVICE_ACCOUNT_INTERACTIVE_LOGIN]);
            }
        },
        [setUpdatedServiceAccount]
    );

    const updateEntities = useCallback(
        (entities: ServiceAccountEntity[]) => {
            if (selectedTab?.name === 'Sources') {
                setUpdatedEntities('devices', entities);
            }
            if (selectedTab?.name === 'Destinations') {
                setUpdatedEntities('resources', entities);
            }
        },
        [selectedTab, setUpdatedEntities]
    );

    const restrictionOptions: RadioGroupOption[] = useMemo(() => {
        if (!selectedTab) {
            return [];
        }
        const options = [
            { title: 'Unrestricted', value: ProtectionTabsOptions.UNRESTRICTED },
            { title: `Limit to selected ${selectedTab.name.toLowerCase()}`, value: ProtectionTabsOptions.RESTRICTED }
        ];
        if (selectedTab?.name === 'Risk') {
            options.push({ title: 'Limit interactive login', value: ProtectionTabsOptions.INTERACTIVE });
        }
        return options;
    }, [selectedTab]);

    const restrictionSelectedValue = useMemo(() => {
        if (!isRestricted) {
            return ProtectionTabsOptions.UNRESTRICTED;
        }
        if (selectedTab?.name === 'Risk') {
            return updatedServiceAccount?.risk_restriction_type === RiskRestrictionTypes.RISK_LEVEL
                ? ProtectionTabsOptions.RESTRICTED
                : ProtectionTabsOptions.INTERACTIVE;
        }
        return ProtectionTabsOptions.RESTRICTED;
    }, [isRestricted, selectedTab, updatedServiceAccount]);

    const onRestrictionChange = useCallback(
        (value: RadioGroupOptionValue) => {
            if (value !== ProtectionTabsOptions.UNRESTRICTED) {
                setRestriction(true);
                if (selectedTab?.name === 'Risk') {
                    setIsRestrictedByRiskLevel(value === ProtectionTabsOptions.RESTRICTED);
                }
            } else {
                setRestriction(false);
            }
        },
        [setRestriction, setIsRestrictedByRiskLevel, selectedTab]
    );

    return (
        <div className="overflow-y-auto h-full">
            {updatedServiceAccount?.is_internal_sa && (
                <div className="flex flex-row w-full border border-[#F7B748] rounded-[12px] bg-[#FFF8E7] p-4 mb-8 text-[#333] font-[Inter] text-[13px] font-normal leading-normal">
                    <Icon icon={Icons.Warning} size={16} className="text-[#F7B748] px-2" />
                    Applying Virtual Fencing to a Silverfort service account may disrupt AD synchronization and system
                    operations.
                </div>
            )}
            <div className="flex flex-col border rounded-lg text-sf h-full overflow-y-auto">
                <div className="border-b flex justify-between border-sf-gray-100 h-18">
                    <div className="flex justify-between items-center gap-4">
                        <Icon
                            size={24}
                            className="border border-sf-gray-100 rounded-lg my-4 ml-4 p-2 text-sf-gray-400"
                            icon={Icons.ShieldAdd}></Icon>
                        <Label className=" text-16" text="Activate Virtual Fencing Policy"></Label>
                    </div>
                    <Toggle
                        disabled={!isEditor}
                        large
                        className="my-6 justify-self-end mr-4"
                        checked={updatedServiceAccount?.is_protected || false}
                        onChange={(checked) => setUpdatedServiceAccount('is_protected', checked)}
                    />
                </div>
                <div className="flex flex-col px-6 gap-4">
                    <div className="flex flex-col gap-4">
                        <Tabs
                            tabs={TabWithLicense}
                            selectedTab={selectedTabId}
                            onClick={setSelectedTabId}
                            disabledTabs={disabledTabs}
                        />
                        {selectedTab && (
                            <div className="flex flex-col gap-2">
                                <div className="h-10 flex items-center">
                                    <RadioGroup
                                        options={restrictionOptions}
                                        disabled={!isEditor || !selectedTab.restrictionFieldName}
                                        onChange={onRestrictionChange}
                                        selectedOption={restrictionSelectedValue}
                                    />
                                </div>
                                {restrictionSelectedValue === ProtectionTabsOptions.RESTRICTED &&
                                    ((['Sources', 'Destinations'].includes(selectedTab.name) && (
                                        <EntitiesPickerTab
                                            disabled={!isEditor}
                                            entityType={selectedTab.name.toLowerCase()}
                                            entities={entities}
                                            setUpdatedEntities={updateEntities}
                                        />
                                    )) ||
                                        (selectedTab.name === 'Protocols' && (
                                            <ProtocolPickerTab
                                                disabled={!isEditor}
                                                protocols={updatedServiceAccount?.protected_protocols || []}
                                                updateProtocols={(protocols) =>
                                                    setUpdatedServiceAccount('protected_protocols', protocols)
                                                }
                                            />
                                        )) ||
                                        (selectedTab.name === 'Risk' && (
                                            <RiskPickerTab
                                                disabled={!isEditor}
                                                riskLevel={updatedServiceAccount?.protection_risk_level}
                                                updateRiskLevel={(riskLevel) =>
                                                    setUpdatedServiceAccount('protection_risk_level', riskLevel)
                                                }
                                            />
                                        )))}
                                {restrictionSelectedValue === ProtectionTabsOptions.INTERACTIVE && (
                                    <InteractiveLoginPickerTab />
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label text="For all other resources" />
                        <div className="flex p-4 justify-between items-center border border-sf-gray-100 rounded-lg w-full h-12 mb-4">
                            <div className="flex h-4 gap-6 text-13 font-sf-gray-800">
                                <span className="flex items-center gap-1">
                                    <Checkbox
                                        disabled={!isEditor}
                                        checked={(updatedServiceAccount?.actions || []).includes('block')}
                                        onChange={() => updateAction('block')}
                                    />
                                    Deny access
                                </span>
                                <span className="flex items-center gap-1">
                                    <Checkbox
                                        disabled={!isEditor}
                                        checked={(updatedServiceAccount?.actions || []).includes('siem')}
                                        onChange={() => updateAction('siem')}
                                    />
                                    Send to SIEM
                                </span>
                            </div>
                            <div className="flex items-center gap-1 text-sf-gray-400">
                                <Icon icon={Icons.CircleInfo} /> Notifications will be sent automatically
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProtectionSettingsTab;
